import PageContainer from "../../../components/global/PageContainer"
import { useContext, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { StoriesContext } from "../../../controllers/stories"
import { StoryView } from "../../../components/stories/StoryView"
import { Story, StorySlide } from "../../../models/stories.models"
import Episode from "../../../models/episode"
import { colors } from "../../../services/config/colors"
import { IosSpinner } from "../../../components/global/IosSpinner"
import { useTranslation } from "react-i18next"

const StoryDetails = () => {
  const { t } = useTranslation()
  const params = useParams()
  const { getStory } = useContext(StoriesContext)

  // get current story
  const [currentStory, setCurrentStory] = useState<Story>()

  useEffect(() => {
    const fetchData = async () => {
      const result = await getStory(params.storyId!)

      if (result) {
        setCurrentStory(parseOldEpisode(result))
      }
    }

    fetchData()
  }, [t])

  // parse old episode into a new story
  const parseOldEpisode = (oldEpisode: Episode): Story => {
    const story: Story = {
      id: oldEpisode.id,
      lang: oldEpisode.lang,
      title: oldEpisode.translation.title,
      description: "",
      learningObjective: "",
      observations: "",
      slides: [],
    }

    for (const slide of oldEpisode.translation.slides) {
      const slideToPush: StorySlide = {
        backgroundColor: colors.strokeLight,
        textColor: colors.text,
        ...(slide.box && { boxColor: colors.backgroundWhite }),
        contents: [],
      }

      if (slide.decor) {
        slideToPush.contents.push({
          type: "media",
          media: {
            type: "decor",
            url: slide.decor,
          },
        })
      }
      if (slide.title) {
        slideToPush.contents.push({
          type: "slide",
          text: slide.title,
        })
      }
      if (slide.description) {
        slideToPush.contents.push({
          type: "slide",
          text: slide.description,
        })
      }
      if (slide.text) {
        slideToPush.contents.push({
          type: "slide",
          text: slide.text,
        })
      }
      if (slide.image) {
        slideToPush.contents.push({
          type: "media",
          media: {
            type: "image",
            url: slide.image.url,
          },
        })
      }
      if (slide.embedUrl) {
        slideToPush.contents.push({
          type: "media",
          media: {
            type: "video",
            url: slide.embedUrl,
          },
        })
      }
      if (slide.captionTitle) {
        slideToPush.contents.push({
          type: "slide",
          text: slide.captionTitle,
        })
      }
      if (slide.caption) {
        slideToPush.contents.push({
          type: "slide",
          text: slide.caption,
        })
      }
      if (slide.author) {
        slideToPush.contents.push({
          type: "slide",
          text: slide.author,
        })
      }
      if (slide.authorTitle) {
        slideToPush.contents.push({
          type: "slide",
          text: slide.authorTitle,
        })
      }

      story.slides.push(slideToPush)
    }

    for (const quiz of oldEpisode.translation.quiz) {
      const slideToPush: StorySlide = {
        backgroundColor: colors.strokeLight,
        textColor: colors.text,
        ...(quiz.box && { boxColor: colors.backgroundWhite }),
        contents: [
          {
            type: "quiz",
            question: quiz.question,
            opt1: quiz.opt1.text,
            opt2: quiz.opt2?.text,
            opt3: quiz.opt3?.text,
            opt4: quiz.opt4?.text,
            correctAnswer: quiz.correctAnswer,
          },
        ],
      }

      story.slides.push(slideToPush)
    }

    return story
  }

  return (
    <PageContainer
      title={currentStory?.title}
      breadcrumbs={[
        { title: t("stories"), path: "/stories" },
        { title: currentStory?.title ?? "" },
      ]}
      withFilters={false}
      withDatePicker={false}
      withExportButton={false}
      paddingTop={24}
      withMinHeightZero
    >
      <IosSpinner visible={!currentStory} absolutePositioned />
      {!!currentStory && <StoryView story={currentStory} />}
    </PageContainer>
  )
}

export default StoryDetails
